import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { useCallback } from "react"
import { useTranslations } from "use-intl"
import {
  ActionTimeline,
  ActionTimelineError,
  ActionTimelineSkeleton,
} from "../../../../../components/ActionTimeline"
import { getCollectionSweepQuoteError } from "../../../../../components/CollectionSweepQuoteReceipt/getCollectionSweepQuoteError"
import type { SchedulableResponse } from "../../../../../polychain/hooks/useScheduler"
import type { TransactionIdentifier } from "../../../../../stores/currentTransactionStore"
import { FlowActions } from "../../../../../stores/flowStore"
import type { CheckoutFlowState } from "../../../../flow-state"
import { useActionErrors } from "../../../../hooks/useErrors"
import { useCollectionSweepQuery } from "../../hooks/useCollectionSweepQuery"
import { CollectionSweepFlowTimeline_quoteFragment } from "./CollectionSweepTimeline.fragment"

type Props = {
  collectionSlug: string
  maxAmountOfItems: number
  maxPricePerItem: number
  recipient?: string
  quote: FragmentOf<typeof CollectionSweepFlowTimeline_quoteFragment>
  onAllActionsCompleted?: (
    response: SchedulableResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => void
}

function getPaymentAsset(
  quote: ResultOf<typeof CollectionSweepFlowTimeline_quoteFragment>,
) {
  if (quote.quote.__typename === "CollectionSweepQuoteNotFound") {
    return {
      chain: quote.collection.chain.identifier,
      address: "0x0000000000000000000000000000000000000000",
    }
  }
  const itemQuote = quote.quote.itemQuotes[0]
  return {
    chain: itemQuote.pricePerItem.token.chain.identifier,
    address: itemQuote.pricePerItem.token.address,
  }
}

export function CollectionSweepTimeline({
  collectionSlug,
  maxAmountOfItems,
  maxPricePerItem,
  recipient,
  quote: quoteKey,
  onAllActionsCompleted: onAllActionsCompletedProp,
}: Props) {
  const t = useTranslations("wallet.CollectionSweepTimeline")

  const quote = readFragment(
    CollectionSweepFlowTimeline_quoteFragment,
    quoteKey,
  )
  const paymentAsset = getPaymentAsset(quote)
  const { actions, error, fetching, refetch } = useCollectionSweepQuery({
    paymentAsset,
    collectionSlug,
    maxAmountOfItems,
    maxPricePerItem: String(maxPricePerItem),
    recipient,
  })

  const onAllActionsCompleted = useCallback(
    (
      response: SchedulableResponse,
      receiptTransactions: TransactionIdentifier[],
    ) => {
      if (response.action === "TRANSACTION") {
        FlowActions.setFlowField<CheckoutFlowState>("transactionIdentifier", {
          chain: response.transactionIdentifier.chain,
          transactionHash: response.transactionIdentifier.hash,
        })
      }

      onAllActionsCompletedProp?.(response, receiptTransactions)
    },
    [onAllActionsCompletedProp],
  )

  const actionError = useActionErrors()

  const quoteError = getCollectionSweepQuoteError(quote.quote)
  if (quoteError) {
    return null
  }

  if (fetching) {
    return <ActionTimelineSkeleton count={1} />
  }

  if (error) {
    return <ActionTimelineError message={t("error")} onRetry={refetch} />
  }

  if (actionError) {
    return <ActionTimelineError message={actionError} onRetry={refetch} />
  }

  return (
    <ActionTimeline
      actions={actions}
      onAllActionsCompleted={onAllActionsCompleted}
      onReload={refetch}
    />
  )
}
